import { useMutation } from "@tanstack/react-query";
import {
  type Api,
  insertWorkoutItem as insertWorkoutItemApi,
} from "api-client";
import {
  type WorkoutFormat,
  type WorkoutItemGroupType,
} from "api-client/legacy-trpc";

import { useMinimapDragAndDropManager } from "src/components/MinimapDragAndDropManager";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutEditStore } from "./WorkoutEditContext";
import { scrollWorkoutItemIntoView } from "./workoutItemScroll";
import { useWorkoutItemsDragAndDropManager } from "./WorkoutItemsDragAndDropManagerContext";

export interface InsertWorkoutItemOptions {
  format?: WorkoutFormat | null;
  groupType?: WorkoutItemGroupType | null;
}

export function useInsertWorkoutItem() {
  const manager = useWorkoutItemsDragAndDropManager();
  const minimapManager = useMinimapDragAndDropManager();
  const workoutEditStore = useWorkoutEditStore();
  const { withSaving } = useAuthenticatedNavigationContext();
  const insertWorkoutItemAtMutation = useMutation({
    mutationFn: ({
      draftWorkoutItemId: _,
      ...data
    }: Api.InsertWorkoutItem.Input & { draftWorkoutItemId: string }) => {
      return withSaving(insertWorkoutItemApi(data));
    },
    onSuccess: (workoutItem, variables) => {
      const insertedWorkoutItem = workoutItem.data.data;

      workoutEditStore.removeItem(variables.draftWorkoutItemId);
      workoutEditStore.insertWorkoutItem(
        variables.body.at,
        insertedWorkoutItem,
      );
      manager.openAccordionItem(insertedWorkoutItem.id);
      minimapManager.expandItem(insertedWorkoutItem.id);
      void scrollWorkoutItemIntoView({
        workoutItemId: insertedWorkoutItem.id,
      });
    },
  });

  function insertWorkoutItem(
    at: number,
    parentId: string | null,
    type: "NORMAL" | "GROUP",
    options?: InsertWorkoutItemOptions,
  ) {
    const draftWorkoutItem = workoutEditStore.createDraftItem(
      at,
      parentId,
      type,
    );

    if (type === "GROUP") {
      const format = options?.format ?? null;
      const groupType = options?.groupType ?? null;

      insertWorkoutItemAtMutation.mutate({
        draftWorkoutItemId: draftWorkoutItem.id,
        body: {
          parentId: parentId,
          at: at,
          groupType: groupType,
          format: format,
          name: "",
        },
        path: {
          workoutId: workoutEditStore.workout.id,
        },
      });
    }
  }

  return insertWorkoutItem;
}
